window.quickAddTileData = window.quickAddTileData || {};

(function () {
  'use strict';

  let fetchService = null;
  let companionFetchService = null;
  let fallbackFetchService = null;

  function createProductService(query) {
    return window.storefrontApi.createNodesChunkedProductsService({
      query,
      chunkSize: 25,
      flushDelay: 30,
    });
  }

  function getFetchService() {
    if (fetchService) return fetchService;

    if (!window.storefrontApi?.createNodesChunkedProductsService || !window.storefrontApi?.QUICK_ADD_PRODUCT_QUERY) {
      throw new Error('[quick-add-tile-data] Storefront API is not available');
    }

    fetchService = createProductService(window.storefrontApi.QUICK_ADD_PRODUCT_QUERY);
    return fetchService;
  }

  function getCompanionFetchService() {
    if (companionFetchService) return companionFetchService;

    if (!window.storefrontApi?.createNodesChunkedProductsService || !window.storefrontApi?.QUICK_ADD_COMPANION_QUERY) {
      return getFetchService();
    }

    companionFetchService = createProductService(window.storefrontApi.QUICK_ADD_COMPANION_QUERY);
    return companionFetchService;
  }

  function getFallbackFetchService() {
    if (fallbackFetchService) return fallbackFetchService;

    if (
      !window.storefrontApi?.createNodesChunkedProductsService ||
      !window.storefrontApi?.QUICK_ADD_PRODUCT_FALLBACK_QUERY
    ) {
      return null;
    }

    fallbackFetchService = createProductService(window.storefrontApi.QUICK_ADD_PRODUCT_FALLBACK_QUERY);
    return fallbackFetchService;
  }

  function normalizeProductGid(productId) {
    const raw = String(productId ?? '').trim();
    if (!raw) return '';

    if (window.utilities?.generateGid) {
      const gid = window.utilities.generateGid(raw);
      if (gid) return gid;
    }

    const gidWithHash = raw.match(/^gid:\/\/shopify\/Product\/(\d+)(?::[a-f0-9]{20,})?$/i);
    if (gidWithHash) {
      return `gid://shopify/Product/${gidWithHash[1]}`;
    }

    const numericWithHash = raw.match(/^(\d+)(?::[a-f0-9]{20,})?$/);
    if (numericWithHash) {
      return `gid://shopify/Product/${numericWithHash[1]}`;
    }

    return raw;
  }

  async function fetchProductNode(productGid, service) {
    const gid = normalizeProductGid(productGid);
    if (!gid || !service) return null;

    const { fetchProductsBatched } = service;
    const result = await fetchProductsBatched([gid]);
    return result?.byId?.get(gid) || result?.list?.[0] || null;
  }

  function normalizeValue(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  function formatMoney(amount, currencyCode) {
    const numeric = parseFloat(amount);
    if (Number.isNaN(numeric)) return '';

    const currency = currencyCode || window.Shopify?.currency?.active || window.currency || 'USD';

    return window.utilities.formatPrice(currency, numeric);
  }

  function getVariants(product) {
    return (product?.variants?.edges || []).map(({ node }) => node).filter(Boolean);
  }

  function isCompleteQuickAddProduct(product) {
    if (!product?.id) return false;
    return Array.isArray(product?.options) && getVariants(product).length > 0;
  }

  function shouldReplaceCachedProduct(existing, incoming) {
    if (!incoming) return false;
    if (!existing) return true;
    if (isCompleteQuickAddProduct(existing) && !isCompleteQuickAddProduct(incoming)) return false;
    if (!isCompleteQuickAddProduct(existing) && isCompleteQuickAddProduct(incoming)) return true;
    return true;
  }

  function getOptionValue(variant, optionName) {
    if (!optionName) return null;
    const match = normalizeValue(optionName);
    const selected = variant?.selectedOptions || [];
    const option = selected.find((entry) => normalizeValue(entry.name) === match);
    return option?.value ?? null;
  }

  function findSizeOption(options, sizeOptionMatch) {
    const match = normalizeValue(sizeOptionMatch);
    return (
      options.find((option) => normalizeValue(option.name) === match) ||
      options.find(
        (option) => normalizeValue(option.name).includes(match) || match.includes(normalizeValue(option.name)),
      )
    );
  }

  function findWidthOption(options, widthRegular) {
    const regular = normalizeValue(widthRegular);
    return options.find((option) => (option.values || []).some((value) => normalizeValue(value) === regular));
  }

  function uniqueOptionValues(variants, optionName) {
    const values = [];
    variants.forEach((variant) => {
      const value = getOptionValue(variant, optionName);
      if (value && !values.includes(value)) {
        values.push(value);
      }
    });
    return values;
  }

  function isWidthAvailable(variants, widthOptionName, widthValue) {
    return variants.some(
      (variant) =>
        variant.availableForSale &&
        normalizeValue(getOptionValue(variant, widthOptionName)) === normalizeValue(widthValue),
    );
  }

  function isSizeAvailable(variants, sizeOptionName, sizeValue, widthOptionName, selectedWidth) {
    return variants.some((variant) => {
      if (!variant.availableForSale) return false;
      if (normalizeValue(getOptionValue(variant, sizeOptionName)) !== normalizeValue(sizeValue)) return false;
      if (widthOptionName && selectedWidth) {
        return normalizeValue(getOptionValue(variant, widthOptionName)) === normalizeValue(selectedWidth);
      }
      return true;
    });
  }

  function buildOptionList(values, isAvailable) {
    return values.map((value) => ({
      id: value,
      label: value,
      available: isAvailable(value),
    }));
  }

  function isProductAvailable(product) {
    if (!product) return false;
    if (product.availableForSale) return true;
    if (typeof product.totalInventory === 'number' && product.totalInventory > 0) return true;
    return getVariants(product).some((variant) => variant.availableForSale);
  }

  function buildFit(product) {
    const meta = getMetafieldMap(product);
    const node = meta.sizeRecommendation;
    if (!node) return null;
    return {
      node: String(node),
      message: meta.sizeRecommendationMessage || '',
    };
  }

  const QUICK_ADD_IMAGE_WIDTHS = [165, 280, 360, 533, 720, 940];
  const QUICK_ADD_IMAGE_SIZES = '(min-width: 60.25rem) 32rem, 280px';
  const QUICK_ADD_IMAGE_DEFAULT_WIDTH = '533';

  function buildQuickAddResponsiveImage(url) {
    const normalizedUrl = String(url || '').trim();
    if (!normalizedUrl) return null;

    if (window.utilities?.generateSrcSet) {
      return window.utilities.generateSrcSet(
        normalizedUrl,
        QUICK_ADD_IMAGE_WIDTHS,
        QUICK_ADD_IMAGE_DEFAULT_WIDTH,
        QUICK_ADD_IMAGE_SIZES,
      );
    }

    return {
      src: normalizedUrl,
      srcset: '',
      sizes: QUICK_ADD_IMAGE_SIZES,
    };
  }

  function applyQuickAddResponsiveImage(img, url) {
    if (!img) return;

    const attrs = buildQuickAddResponsiveImage(url);
    if (!attrs) return;

    img.src = attrs.src;
    if (attrs.srcset) {
      img.srcset = attrs.srcset;
    } else {
      img.removeAttribute('srcset');
    }
    if (attrs.sizes) {
      img.sizes = attrs.sizes;
    } else {
      img.removeAttribute('sizes');
    }
    img.dataset.imageSource = String(url || '').trim();
  }

  function getProductImageUrl(product) {
    return product?.featuredImage?.url || '';
  }

  function getProductImageAlt(product) {
    return product?.featuredImage?.altText || product?.title || '';
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getMetafieldMap(product) {
    const map = {};
    const entries = Array.isArray(product?.metafields) ? product.metafields : [];
    entries.forEach((entry) => {
      if (entry?.key) {
        map[entry.key] = entry.value;
      }
    });
    return map;
  }

  function getShopCountry() {
    return window.Shopify?.country || 'US';
  }

  function getLocalePromoMessage(meta) {
    const locale = (window.localeCode || 'en-us').toLowerCase();

    if (locale === 'fr-ca') {
      return meta.promo_applied_message_ca_fr || meta.promotional_message_ca_fr || '';
    }
    if (locale === 'en-ca') {
      return meta.promo_applied_message_ca_en || meta.promotional_message_ca_en || '';
    }
    return meta.promo_applied_message_us_en || meta.promotional_message_us_en || '';
  }

  function isTruthyFlag(value) {
    return value === true || value === 'true' || value === '1';
  }

  function isEarlyAccess(meta) {
    const country = getShopCountry();
    const earlyBuy = isTruthyFlag(meta[`loyaltyEarlyBuy${country}`]);
    const endDate = meta[`loyaltyEarlyBuyEndDate${country}`];

    if (!earlyBuy || !endDate) return false;

    const endTimestamp = new Date(endDate).getTime() / 1000;
    return Number.isFinite(endTimestamp) && endTimestamp > Date.now() / 1000;
  }

  function getVariantPricing(product) {
    const variant = product?.selectedOrFirstAvailableVariant;
    const priceAmount = variant?.price?.amount ?? product?.priceRange?.maxVariantPrice?.amount;
    const compareAmount = variant?.compareAtPrice?.amount ?? product?.compareAtPriceRange?.maxVariantPrice?.amount;
    const currency =
      variant?.price?.currencyCode ??
      product?.priceRange?.maxVariantPrice?.currencyCode ??
      window.Shopify?.currency?.active ??
      window.currency ??
      'USD';

    const price = parseFloat(priceAmount || '0');
    const compareAt = parseFloat(compareAmount || '0');

    return {
      priceAmount,
      compareAmount,
      currency,
      price,
      compareAt,
      onSale: compareAt > price && price > 0,
      available: variant?.availableForSale ?? product?.availableForSale ?? false,
    };
  }

  function getTileStrings(strings = {}) {
    const tile = window.productTileStrings || {};
    return {
      soldOut: strings.labelSoldOut || tile.soldOut || 'Sold out',
      preorder: strings.labelPreorder || tile.preorder || 'Pre-order',
      comingSoon: strings.labelComingSoon || tile.comingSoon || 'Coming soon',
      earlyAccess: strings.labelEarlyAccess || tile.earlyAccess || 'Early access',
      onlineExclusive: strings.labelOnlineExclusive || tile.onlineExclusive || 'Online exclusive',
      noPromo: strings.labelNoPromo || tile.noPromo || 'Excluded from promotions',
      finalSale: strings.labelFinalSale || tile.finalSale || 'Final Sale',
      finalSaleCopy: strings.labelFinalSaleCopy || tile.finalSaleCopy || 'This item cannot be returned',
      limited: strings.labelLimited || 'Limited time only',
      sale: strings.labelSale || 'Sale',
      crewExclusive: strings.labelCrewExclusive || 'Crew Exclusive',
      exclusivePrice: strings.labelExclusivePrice || tile.membersOnlyPrice || 'Crew Exclusive Price',
      priceRegular: strings.priceRegular || tile.regular_price || 'Regular price',
      priceSale: strings.priceSale || tile.sale_price || 'Sale price',
    };
  }

  function isEnableBadges(strings = {}) {
    const value = strings.enableBadges;
    return value !== 'false' && value !== false;
  }

  function buildLabelSpan(text, { role, type, className }) {
    const isPrimary = role === 'primary';
    let classNames = isPrimary
      ? 'product-label_primary-label fixed-utility-m-em'
      : 'product-label_secondary-label fixed-utility-m';

    if (isPrimary && type === 'problem_child') {
      classNames = 'product-label_primary-label fixed-utility-m';
    }

    if (type) {
      classNames += ` product-label_${isPrimary ? 'primary' : 'secondary'}-label-${type}`;
    }
    if (className) {
      classNames += ` product-label_${isPrimary ? 'primary' : 'secondary'}-label-${className}`;
    }

    return `<span class="${classNames}">${escapeHtml(text)}</span>`;
  }

  function buildProductLabelMarkup(product, strings = {}) {
    const meta = getMetafieldMap(product);
    const t = getTileStrings(strings);
    const tags = (product?.tags || []).map((tag) => String(tag).toLowerCase());
    const hasTag = (tag) => tags.includes(String(tag).toLowerCase());
    const country = getShopCountry();
    const available = isProductAvailable(product);
    const preOrder = isTruthyFlag(meta[`preOrder${country}`]);
    const comingSoon = isTruthyFlag(meta[`comingSoon${country}`]);
    const promoMessage = getLocalePromoMessage(meta).trim();
    const earlyAccess = isEarlyAccess(meta);
    const onlineExclusive = isTruthyFlag(meta.onlineExclusive);
    const nonReturnable =
      Boolean(meta.non_returnable_type) && String(meta.non_returnable_type).toLowerCase() !== 'hygiene';
    const onSale = getVariantPricing(product).onSale;
    const enableBadges = isEnableBadges(strings);
    const isProductInStock = available || comingSoon;

    let keepCheckingPrimary = true;
    let keepCheckingSecondary = true;
    let primary = '';
    let primaryType = '';
    let primaryClass = '';
    let secondary = '';
    let secondaryType = '';
    let secondaryClass = '';

    if (!isProductInStock) {
      keepCheckingPrimary = false;
      if (hasTag('notify')) {
        // PDP leaves labels empty for notify-me sold out products.
      } else if (preOrder) {
        primary = t.preorder;
        primaryType = 'inventory';
      } else if (hasTag('comingsoon')) {
        primary = t.comingSoon;
        primaryType = 'inventory';
        primaryClass = 'loyal';
      } else {
        primary = t.soldOut;
        primaryType = 'inventory';
        primaryClass = 'promo';
      }
    }

    if (keepCheckingPrimary && comingSoon) {
      primary = t.comingSoon;
      primaryType = 'inventory';
      primaryClass = 'loyal';
      keepCheckingPrimary = false;
    }

    if (keepCheckingPrimary) {
      if (earlyAccess) {
        keepCheckingPrimary = false;
        primary = t.crewExclusive;
        primaryType = 'inventory';
        primaryClass = 'loyal';
      }
      if (onlineExclusive) {
        keepCheckingPrimary = false;
        primary = t.onlineExclusive;
        primaryType = 'problem_child';
        primaryClass = '';
      }
    }

    if (keepCheckingSecondary && earlyAccess) {
      keepCheckingSecondary = false;
      secondary = t.earlyAccess;
      secondaryType = 'early';
    }

    if (keepCheckingSecondary && !hasTag('comingsoon') && !preOrder) {
      if (hasTag('excludedfrompromotions')) {
        secondary = t.noPromo;
        secondaryType = 'promotion';
      } else if (promoMessage) {
        secondary = promoMessage;
        secondaryType = 'promotion_message';
      } else if (onlineExclusive) {
        const onlineExclusiveLabel = t.onlineExclusive;
        secondary = onlineExclusiveLabel;
        secondaryType = 'problem_child';
        if (primary === onlineExclusiveLabel) {
          keepCheckingPrimary = true;
          primary = '';
          primaryType = '';
          primaryClass = '';
        }
      } else if (nonReturnable) {
        secondary = t.finalSaleCopy;
        secondaryType = 'return';
      }
    }

    if (keepCheckingPrimary && nonReturnable) {
      keepCheckingPrimary = false;
      primary = t.finalSale;
      primaryType = 'final_sale';
      primaryClass = 'promo';
    }

    if (keepCheckingPrimary && secondaryType === 'promotion_message') {
      keepCheckingPrimary = false;
      primary = t.limited;
      primaryType = 'sale';
      primaryClass = 'promo';
    }

    if (keepCheckingPrimary && !enableBadges && onSale) {
      primary = t.sale;
      primaryType = 'sale';
      primaryClass = 'promo';
    }

    if (!primary && !secondary) return '';

    let markup = '<div class="product-label">';

    if (primary) {
      markup += buildLabelSpan(primary, { role: 'primary', type: primaryType, className: primaryClass });
    }

    if (secondary) {
      const secondaryText = primary ? ` - ${secondary}` : secondary;
      markup += buildLabelSpan(secondaryText, {
        role: 'secondary',
        type: secondaryType,
        className: secondaryClass,
      });
    }

    markup += '</div>';
    return markup;
  }

  function buildPriceMarkup(product, strings = {}) {
    const meta = getMetafieldMap(product);
    const t = getTileStrings(strings);
    const enableBadges = isEnableBadges(strings);
    const pricing = getVariantPricing(product);
    const { priceAmount, compareAmount, currency, onSale, available } = pricing;
    const country = getShopCountry();
    const crewPrice = parseFloat(meta[`crewExclusivePrice${country}`] || '0');
    const hasCrewPrice = crewPrice > 0;
    const isCustomerLoyal = Boolean(window.customer?.id && window.customer?.loyaltyStatus === 'active');
    const earlyAccess = isEarlyAccess(meta);

    const formattedPrice = priceAmount ? formatMoney(priceAmount, currency) : '';
    const formattedCompare = onSale && compareAmount ? formatMoney(compareAmount, currency) : '';
    const formattedCrew = hasCrewPrice ? formatMoney(crewPrice, currency) : '';

    const priceClasses = ['price', 'price--large', 'price--show-badge'];
    if (!available) priceClasses.push('price--sold-out');
    if (onSale) priceClasses.push('price--on-sale');

    let wrapperInner;

    if (hasCrewPrice) {
      if (isCustomerLoyal) {
        wrapperInner = `
          <span id="main-price" class="price-item price-item--loyalty-color fixed-heading-s">${escapeHtml(formattedCrew)}</span>
          <span class="price-item price-item--regular fluid-speciality-markdown-price-m">${escapeHtml(formattedPrice)}</span>
          <span class="price-item--crew-label fixed-body-s price-item--loyalty-color">${escapeHtml(t.exclusivePrice)}</span>`;
      } else {
        wrapperInner = `
          <span id="main-price" class="price-item fixed-heading-s">${escapeHtml(formattedPrice)}</span>
          <span class="price-item price-item--loyalty-color fixed-heading-s">${escapeHtml(formattedCrew)}</span>
          <span class="price-item--crew-label fixed-body-s price-item--loyalty-color">${escapeHtml(t.exclusivePrice)}</span>`;
      }
    } else if (onSale) {
      wrapperInner = `
        <span class="visually-hidden visually-hidden--inline">${escapeHtml(t.priceRegular)}</span>
        <span id="main-price" class="price-item price-item--sale fixed-heading-s">${escapeHtml(formattedPrice)}</span>
        <span class="visually-hidden visually-hidden--inline">${escapeHtml(t.priceSale)}</span>
        <span>
          <s class="price-item price-item--regular fluid-speciality-markdown-price-m">${escapeHtml(formattedCompare)}</s>
        </span>
        ${
          enableBadges
            ? `<span class="price__badge__wrapper price__badge__wrapper--sale">
          <span class="price__badge fixed-utility-s-em">${escapeHtml(t.sale)}</span>
        </span>`
            : ''
        }`;
    } else {
      const loyaltyClass = earlyAccess ? ' price-item--loyalty-color' : '';
      wrapperInner = `
        <small class="unit-price caption">
          <span id="main-price" class="price-item price-item--last${loyaltyClass}">
            <span class="fixed-heading-s">${escapeHtml(formattedPrice)}</span>
          </span>
        </small>`;
    }

    const wrapperClass = hasCrewPrice ? 'price__wrapper price__wrapper--crew' : 'price__wrapper';

    return `
      <div class="${priceClasses.join(' ')}">
        <div class="price__container">
          <div class="${wrapperClass}">
            ${wrapperInner}
          </div>
        </div>
      </div>`;
  }

  function buildSummaryParts(product, strings = {}) {
    try {
      return {
        labelsHtml: buildProductLabelMarkup(product, strings),
        priceHtml: buildPriceMarkup(product, strings),
      };
    } catch (error) {
      console.warn('[quick-add-tile-data] failed to build summary markup', error);
      return { labelsHtml: '', priceHtml: '' };
    }
  }

  function productUrlFromNode(product) {
    const handle = product?.handle;
    if (!handle) return '';
    return `/products/${handle}`;
  }

  function productSummaryFromNode(product, strings = {}) {
    const currency = product?.priceRange?.maxVariantPrice?.currencyCode || window.currency || 'USD';
    const pricing = getVariantPricing(product);
    const price = pricing.priceAmount ? formatMoney(pricing.priceAmount, currency) : '';
    const compareAtPrice =
      pricing.onSale && pricing.compareAmount ? formatMoney(pricing.compareAmount, currency) : null;
    const summaryParts = buildSummaryParts(product, strings);
    const meta = getMetafieldMap(product);

    return {
      title: product?.title || '',
      handle: product?.handle || '',
      image: getProductImageUrl(product),
      imageAlt: getProductImageAlt(product),
      price,
      compareAtPrice,
      fit: buildFit(product),
      gender: meta.gender || null,
      kidsAge: meta.kidsAge || null,
      url: productUrlFromNode(product),
      summaryParts,
      isGiftCard: Boolean(product?.isGiftCard),
    };
  }

  function buildSizeGenderLabel(gender, kidsAge, labels = {}) {
    const value = String(gender || '').trim();
    if (!value || value === 'Unisex') return '';

    if (value === 'Mens') return labels.mens || '';
    if (value === 'Womens') return labels.womens || '';

    if (value === 'Boys' || value === 'Girls') {
      const ageKey = String(kidsAge || '')
        .trim()
        .toLowerCase();
      return labels.kids?.[ageKey] || '';
    }

    return '';
  }

  async function enrichColours(colours, prefetchedProducts = new Map()) {
    if (!Array.isArray(colours) || colours.length === 0) {
      return { colours: [], productsById: new Map(prefetchedProducts) };
    }

    const byId = new Map(prefetchedProducts);
    const missingIds = colours.map((colour) => normalizeProductGid(colour.id)).filter((id) => id && !byId.has(id));

    if (missingIds.length > 0) {
      try {
        const { fetchProductsBatched } = getCompanionFetchService();
        const result = await fetchProductsBatched([...new Set(missingIds)]);
        result?.byId?.forEach((product, id) => {
          const existing = byId.get(id);
          if (shouldReplaceCachedProduct(existing, product)) {
            byId.set(id, product);
          }
        });
      } catch (error) {
        console.warn('[quick-add-tile-data] failed to enrich colour products', error);
      }
    }

    const enriched = colours
      .map((colour) => {
        const colourGid = normalizeProductGid(colour.id);
        const product = byId.get(colourGid) || byId.get(colour.id);
        if (!product) return null;

        const heroImage = product.featuredImage?.url || colour.heroImage || colour.image || '';

        return {
          ...colour,
          id: colourGid,
          available: isProductAvailable(product),
          heroImage,
          image: heroImage || colour.image,
        };
      })
      .filter(Boolean);

    return { colours: enriched, productsById: byId };
  }

  function mergeColourEnrichment(cache, enrichmentById) {
    if (!cache || !enrichmentById) return cache;
    enrichmentById.forEach((product, id) => {
      if (shouldReplaceCachedProduct(cache.get(id), product)) {
        cache.set(id, product);
      }
    });
    return cache;
  }

  function filterVisibleColours(colours, activeProductGid) {
    if (!Array.isArray(colours) || colours.length === 0) return [];

    const available = colours.filter((colour) => colour.available !== false);
    if (available.length > 0) return available;

    const activeGid = normalizeProductGid(activeProductGid);
    const active = colours.find((colour) => normalizeProductGid(colour.id) === activeGid);
    return active ? [active] : colours.slice(0, 1);
  }

  async function fetchQuickAddProduct(productGid) {
    const gid = normalizeProductGid(productGid);
    if (!gid) {
      throw new Error('[quick-add-tile-data] Missing product GID');
    }

    const product = await fetchProductNode(gid, getFetchService());
    if (product) return product;

    const fallbackService = getFallbackFetchService();
    const fallbackProduct = await fetchProductNode(gid, fallbackService);
    if (fallbackProduct) {
      console.warn('[quick-add-tile-data] loaded product via fallback query', gid);
      return fallbackProduct;
    }

    throw new Error(`[quick-add-tile-data] Product not found for ${gid}`);
  }

  function cacheQuickAddProduct(cache, product) {
    if (!cache || !product?.id) return;
    cache.set(product.id, product);
  }

  async function resolveQuickAddProduct(productGid, cache = new Map()) {
    const gid = normalizeProductGid(productGid);
    if (!gid) {
      throw new Error('[quick-add-tile-data] Missing product GID');
    }

    const cached = cache.get(gid) || cache.get(productGid);
    if (cached && isCompleteQuickAddProduct(cached)) return cached;

    const product = await fetchQuickAddProduct(gid);
    cacheQuickAddProduct(cache, product);
    return product;
  }

  function mergeProductCache(target, source) {
    if (!target || !source) return target;
    source.forEach((product, id) => {
      if (shouldReplaceCachedProduct(target.get(id), product)) {
        target.set(id, product);
      }
    });
    return target;
  }

  function buildQuickAddOptions(product, strings = {}, selectedWidth = null) {
    const options = Array.isArray(product?.options) ? product.options : [];
    const variants = getVariants(product);
    const sizeOption = findSizeOption(options, strings.sizeOptionMatch || 'size');
    const widthOption = findWidthOption(options, strings.widthRegular || 'regular');
    const widthOptionName = widthOption?.name || null;
    const sizeOptionName = sizeOption?.name || null;

    // Gift cards: map the first option (denominations) to the size slot so the
    // existing grid UI and variant resolution work without changes.
    if (product?.isGiftCard && !sizeOptionName && options.length > 0) {
      const denomOption = options[0];
      return {
        widths: [],
        sizes: buildOptionList(uniqueOptionValues(variants, denomOption.name), (value) =>
          isSizeAvailable(variants, denomOption.name, value, null, null),
        ),
        variants,
        optionMeta: {
          sizeOptionName: denomOption.name,
          widthOptionName: null,
          isGiftCard: true,
          denominationOptionName: denomOption.name,
        },
        fit: null,
      };
    }

    const widths = widthOptionName
      ? buildOptionList(uniqueOptionValues(variants, widthOptionName), (value) =>
          isWidthAvailable(variants, widthOptionName, value),
        )
      : [];

    const sizes = sizeOptionName
      ? buildOptionList(uniqueOptionValues(variants, sizeOptionName), (value) =>
          isSizeAvailable(variants, sizeOptionName, value, widthOptionName, selectedWidth),
        )
      : [];

    return {
      widths,
      sizes,
      variants,
      optionMeta: {
        sizeOptionName,
        widthOptionName,
      },
      fit: buildFit(product),
    };
  }

  function updateSizeAvailability(sizes, variants, optionMeta, selectedWidth) {
    const { sizeOptionName, widthOptionName } = optionMeta || {};
    if (!sizeOptionName) return sizes || [];

    return (sizes || []).map((size) => ({
      ...size,
      available: isSizeAvailable(variants, sizeOptionName, size.id, widthOptionName, selectedWidth),
    }));
  }

  function resolveVariantId(variants, selection, optionMeta) {
    const match = resolveSelectedVariant(variants, selection, optionMeta);
    if (!match?.id || !match.availableForSale) return null;
    return window.utilities?.extractIdFromGid
      ? window.utilities.extractIdFromGid(match.id)
      : String(match.id).replace(/\D/g, '');
  }

  function resolveSelectedVariant(variants, selection, optionMeta) {
    const { sizeOptionName, widthOptionName } = optionMeta || {};
    const variantList = variants || [];

    if (!sizeOptionName) {
      if (variantList.length === 1) return variantList[0];
      return variantList.find((variant) => variant.availableForSale) || variantList[0] || null;
    }

    if (!selection?.size) return null;

    const matches = variantList.filter((variant) => {
      if (normalizeValue(getOptionValue(variant, sizeOptionName)) !== normalizeValue(selection.size)) {
        return false;
      }
      if (widthOptionName && selection.width) {
        return normalizeValue(getOptionValue(variant, widthOptionName)) === normalizeValue(selection.width);
      }
      return true;
    });

    return matches.find((variant) => variant.availableForSale) || matches[0] || null;
  }

  function shouldSuppressLowStockMessaging(product) {
    const meta = getMetafieldMap(product);
    const tags = (product?.tags || []).map((tag) => String(tag).toLowerCase());
    const country = getShopCountry();

    if (tags.includes('comingsoon')) return true;
    if (isTruthyFlag(meta[`preOrder${country}`])) return true;
    if (isTruthyFlag(meta[`comingSoon${country}`])) return true;

    const comingSoonReleaseKey = `comingSoonReleaseDate${country}`;
    const comingSoonRelease = meta[comingSoonReleaseKey];
    if (isTruthyFlag(meta[`comingSoon${country}`]) && comingSoonRelease) {
      const releaseTimestamp = new Date(comingSoonRelease).getTime();
      if (Number.isFinite(releaseTimestamp) && releaseTimestamp / 1000 > Date.now() / 1000) {
        return true;
      }
    }

    return false;
  }

  function getLowStockThreshold() {
    const threshold = Number(window.product?.lowStockValue ?? 0);
    return Number.isFinite(threshold) ? threshold : 0;
  }

  function buildLowStockMessage(product, selection = {}, optionMeta = {}) {
    if (!product || shouldSuppressLowStockMessaging(product)) return '';

    const { sizeOptionName } = optionMeta || {};
    const variants = getVariants(product);

    if (sizeOptionName && !selection?.size) return '';

    const variant = resolveSelectedVariant(variants, selection, optionMeta);
    if (!variant) return '';

    const quantity = variant.quantityAvailable;
    if (quantity == null || !Number.isFinite(Number(quantity))) return '';

    const threshold = getLowStockThreshold();
    const qty = Number(quantity);
    const oosTemplate = window.product?.out_of_stock_variant ?? '';
    const lowstockTemplate = window.product?.lowstock ?? '';

    if (qty <= 0 && !variant.availableForSale) {
      const sizeValue = selection?.size || getOptionValue(variant, sizeOptionName) || '';
      if (!oosTemplate || !sizeValue) return '';
      return oosTemplate.replace('{{ size_value }}', sizeValue);
    }

    if (qty > 0 && threshold > 0 && qty <= threshold && lowstockTemplate) {
      return lowstockTemplate.replace('{{ quantity }}', String(threshold));
    }

    return '';
  }

  function buildLowStockMarkup(product, selection = {}, optionMeta = {}) {
    const message = buildLowStockMessage(product, selection, optionMeta);
    if (!message) return '';

    return `<div class="low-inventory-message"><span class="low-inventory-message--text fixed-utility-s-em">${escapeHtml(message)}</span></div>`;
  }

  window.quickAddTileData.fetchQuickAddProduct = fetchQuickAddProduct;
  window.quickAddTileData.resolveQuickAddProduct = resolveQuickAddProduct;
  window.quickAddTileData.mergeProductCache = mergeProductCache;
  window.quickAddTileData.enrichColours = enrichColours;
  window.quickAddTileData.filterVisibleColours = filterVisibleColours;
  window.quickAddTileData.isProductAvailable = isProductAvailable;
  window.quickAddTileData.buildQuickAddOptions = buildQuickAddOptions;
  window.quickAddTileData.updateSizeAvailability = updateSizeAvailability;
  window.quickAddTileData.resolveVariantId = resolveVariantId;
  window.quickAddTileData.resolveSelectedVariant = resolveSelectedVariant;
  window.quickAddTileData.buildLowStockMessage = buildLowStockMessage;
  window.quickAddTileData.buildLowStockMarkup = buildLowStockMarkup;
  window.quickAddTileData.isCompleteQuickAddProduct = isCompleteQuickAddProduct;
  window.quickAddTileData.mergeColourEnrichment = mergeColourEnrichment;
  window.quickAddTileData.normalizeProductGid = normalizeProductGid;
  window.quickAddTileData.buildSummaryParts = buildSummaryParts;
  window.quickAddTileData.productSummaryFromNode = productSummaryFromNode;
  window.quickAddTileData.buildSizeGenderLabel = buildSizeGenderLabel;
  window.quickAddTileData.buildQuickAddResponsiveImage = buildQuickAddResponsiveImage;
  window.quickAddTileData.applyQuickAddResponsiveImage = applyQuickAddResponsiveImage;
  window.quickAddTileData.productUrlFromNode = productUrlFromNode;
  window.quickAddTileData.__resetServiceForTests = () => {
    fetchService = null;
    companionFetchService = null;
    fallbackFetchService = null;
  };
})();
